/* ─────────────────────────────────────────────────────────────
   Medicore guarantee banner

   Drives the mobile carousel. The scrolling itself is native — the
   CSS does the snapping — so this file only advances the scroll
   position on a timer and keeps the dots in sync. That means a swipe
   never fights a transform, and the row degrades to a plain
   scrollable strip if this script fails to load.

   Autoplay yields to the visitor: it pauses on touch, on hover, on
   keyboard focus and while the tab is hidden, and it stops entirely
   for anyone who has asked for reduced motion.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Matches the 768px grid breakpoint in guarantee-banner.css. Above
  // it the track is a static grid and the carousel must stand down.
  var MOBILE_QUERY = '(max-width: 767px)';

  if (typeof HTMLElement !== 'function') return;

  var McGuarantee = class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gb-track]');
      if (!this.track || this.dataset.carousel !== 'true') return;

      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-gb-slide]'));
      if (this.slides.length < 2) return;

      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-gb-dot]'));
      this.index = 0;
      this.paused = false;
      this.timer = null;

      this.interval = parseInt(this.dataset.interval, 10);
      if (isNaN(this.interval) || this.interval < 1500) this.interval = 4000;

      this.mq = window.matchMedia(MOBILE_QUERY);

      this.bind();
      this.sync();
      this.evaluate();
    }

    disconnectedCallback() {
      this.stop();
      if (this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onMediaChange && this.mq) {
        if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMediaChange);
        else if (this.mq.removeListener) this.mq.removeListener(this.onMediaChange);
      }
    }

    bind() {
      var self = this;

      // Reading the active slide back from scrollLeft keeps the dots
      // honest whether the move came from the timer or a finger.
      var raf = null;
      this.onScroll = function () {
        if (raf) return;
        raf = window.requestAnimationFrame(function () {
          raf = null;
          self.sync();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          self.goTo(parseInt(dot.dataset.gbDot, 10), true);
        });
      });

      // A touch that has begun is an active reader; don't move under it.
      this.track.addEventListener('pointerdown', function () { self.paused = true; }, { passive: true });
      this.track.addEventListener('pointerup', function () { self.resumeSoon(); }, { passive: true });
      this.track.addEventListener('pointercancel', function () { self.resumeSoon(); }, { passive: true });

      this.addEventListener('mouseenter', function () { self.paused = true; });
      this.addEventListener('mouseleave', function () { self.paused = false; });
      this.addEventListener('focusin', function () { self.paused = true; });
      this.addEventListener('focusout', function () { self.paused = false; });

      this.onVisibility = function () { self.paused = document.hidden; };
      document.addEventListener('visibilitychange', this.onVisibility);

      this.onMediaChange = function () { self.evaluate(); };
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMediaChange);
      else if (this.mq.addListener) this.mq.addListener(this.onMediaChange);
    }

    // Start or stop depending on viewport and settings. Called on load
    // and whenever the breakpoint is crossed, so a desktop resize never
    // leaves a timer scrolling a static grid.
    evaluate() {
      var wants =
        this.mq.matches &&
        this.dataset.autoplay === 'true' &&
        !reduceMotion;

      if (wants) this.start();
      else this.stop();

      if (!this.mq.matches) this.track.scrollLeft = 0;
    }

    // Scroll offset of a slide, measured from the first one. Going
    // through offsetLeft alone would fold in the track's own padding
    // and its offset parent; the difference cancels both.
    offsetOf(index) {
      return this.slides[index].offsetLeft - this.slides[0].offsetLeft;
    }

    sync() {
      var self = this;
      var pos = this.track.scrollLeft;
      var best = 0;
      var bestDistance = Infinity;

      this.slides.forEach(function (slide, i) {
        var distance = Math.abs(self.offsetOf(i) - pos);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });

      this.index = best;
      this.dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === best);
        dot.setAttribute('aria-current', i === best ? 'true' : 'false');
      });
    }

    goTo(index, fromUser) {
      var total = this.slides.length;
      if (isNaN(index)) return;

      this.index = ((index % total) + total) % total;

      this.track.scrollTo({
        left: this.offsetOf(this.index),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });

      if (fromUser) this.restart();
    }

    // Advancing past the last slide rewinds to the start. The rewind is
    // the one jump the visitor sees, so it is worth it only when the
    // row genuinely loops rather than snapping back mid-read.
    next() {
      var maxScroll = this.track.scrollWidth - this.track.clientWidth;
      var atEnd = this.track.scrollLeft >= maxScroll - 2;
      this.goTo(atEnd ? 0 : this.index + 1, false);
    }

    resumeSoon() {
      var self = this;
      // Let the browser's momentum scroll settle before the timer may
      // move the track again.
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(function () {
        self.paused = false;
      }, 600);
    }

    start() {
      var self = this;
      this.stop();
      this.timer = window.setInterval(function () {
        if (!self.paused) self.next();
      }, this.interval);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restart() {
      if (!this.timer) return;
      this.start();
    }
  };

  if (!customElements.get('mc-guarantee')) {
    customElements.define('mc-guarantee', McGuarantee);
  }
})();
